import * as Immutable from 'immutable';
import * as React from 'react';
import { useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { removeBlinkFlashMessage, removeFlashMessage } from '../../actions/FlashMessagesActions';
import { StateType } from '../../reducers';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

import Main from './Main';
import './FlashMessages.scss';

/**
 * Map of named click handlers that flash messages can reference by string key
 * (via `options.props.linkHandler` on the dispatched message). Functions cannot
 * be safely round-tripped through redux/Immutable, so callers register the
 * handler here and reference it by name in the dispatched payload.
 *
 * Example:
 *   <FlashMessages location="top" linkHandlers={{ resendVerifyEmail: this.handler }} />
 *
 *   dispatchAddFlashMessages([{...}], {
 *     inputType: 'button',
 *     props: { linkHandler: 'resendVerifyEmail' },
 *     links: [...]
 *   });
 */
export type FlashMessageLinkHandlers = { [key: string]: (event?: React.SyntheticEvent) => void };

interface FlashMessagesProps {
  location: string;
  handleCleanTabCodeError?: () => void;
  linkHandlers?: FlashMessageLinkHandlers;
  showCloseButton?: boolean;
}

const FlashMessages = (props: FlashMessagesProps) => {
  const dispatch = useAppDispatch();

  // @ts-ignore
  const { messages, contexts }: { messages: any; contexts: Immutable.List<any> } = useSelector(
    (state: StateType) => ({
      messages: state.getIn(['flashMessages', 'messages', props.location]),
      contexts: state.getIn(['flashMessages', 'context']),
    }),
    shallowEqual
  );

  const dispatchRemoveFlashMessage = useCallback((path: string[], id: string) => dispatch(removeFlashMessage(path, id)), [dispatch]);
  const dispatchRemoveBlinkFlashMessage = useCallback(
    (path: string[], id: string) => dispatch(removeBlinkFlashMessage(path, id)),
    [dispatch]
  );

  const filterByCtx = () => {
    let msgs = Immutable.List();
    if (messages && contexts) {
      contexts.forEach((ctx: string) => {
        if (messages.get(ctx)) {
          msgs = msgs.push(messages.get(ctx));
        }
      });
    }

    return msgs;
  };

  const handleRemove = (path: string[], id: string) => {
    if (props.handleCleanTabCodeError) {
      props.handleCleanTabCodeError();
    }
    dispatchRemoveFlashMessage(path, id);
  };

  const { location, showCloseButton = true, linkHandlers } = props;
  const messagesFiltered = filterByCtx();
  if (messagesFiltered.size !== 0) {
    return (
      <div className="flash-messages-container">
        <Main
          allMessages={messagesFiltered}
          location={location}
          handleRemove={handleRemove}
          handleRemoveBlink={dispatchRemoveBlinkFlashMessage}
          showCloseButton={showCloseButton}
          linkHandlers={linkHandlers}
        />
      </div>
    );
  } else {
    return null;
  }
};

export default FlashMessages;
